import React from "react";
import {Link} from "../raw-components/link/link";
import {DomEventSubscription} from "../../../utils/event-listener-registration";
import {OrbitComponentWithState} from "../base-components/orbit-component-with-state/orbit-component-with-state";
import {SideMenuLinkConfiguration} from "../../services/configurations/config-data-sections/side-menu.configuration";
import {StickyNavigationMenuListBox} from "./boxes/sticky-navigation-menu-list/sticky-navigation-menu-list.box";
import {StickyNavigationMenuItemBox} from "./boxes/sticky-navigation-menu-item/sticky-navigation-menu-item.box";
import {StickyNavigationMenuItemIconBox} from "./boxes/sticky-navigation-menu-item/sticky-navigation-menu-item-icon.box";
import {StickyNavigationMenuActiveIndicatorComponent} from "./sticky-navigation-menu-active-indicator.component";

interface IStickyNavigationMenuListComponentState {
    activeLink: string;
    activePosition: number;
}

export class StickyNavigationMenuListComponent extends OrbitComponentWithState<{}, IStickyNavigationMenuListComponentState> {

    private allMenuLinks: {[key: string]: SideMenuLinkConfiguration} = {};

    private validNavPaths: string[] = [];
    private activeElement: HTMLElement = null;
    private stickyMenuBoundingBox: DOMRect = null;

    private resizeEventHandler: DomEventSubscription = null;

    constructor(props) {
        super(props);

        this.state = {
            activeLink: "/",
            activePosition: 0
        }
    }

    componentDidMount() {
        this._setActiveLink(this.services.location.currentUri.pathName.toString());
        this.services.location.onBeforeChangingLocation((uri) => this._setActiveLink(uri.pathName.toString()));
        this.resizeEventHandler = this.services.window.subscribeTo.resize(() => this._setActiveLinkPosition());
    }

    componentWillUnmount() {
        this.resizeEventHandler.dispose();
    }

    private _setActiveLinkPosition() {
        const elBoundingBox = this.activeElement.getBoundingClientRect();
        const stickyMenuLeftEdge = this.stickyMenuBoundingBox ? this.stickyMenuBoundingBox.left : 0;
        const elBoundingBoxCenter = (elBoundingBox.left + elBoundingBox.width / 2) - stickyMenuLeftEdge;

        this.setState({activePosition: elBoundingBoxCenter});
    }

    private _setActiveLinkRef(element: HTMLElement, isActive: boolean): void {
        if (element && isActive && element !== this.activeElement) {
            this.activeElement = element;
            this._setActiveLinkPosition();
        }
    }

    private _setStickyMenuContainerRef(element: HTMLElement): void {
        if (element && this.stickyMenuBoundingBox === null) {
            this.stickyMenuBoundingBox = element.getBoundingClientRect();
            this._setActiveLinkPosition();
        }
    }

    private _setActiveLink(link: string) {
        if (this.validNavPaths.indexOf(link) >= 0) {
            this.setState({activeLink: link});
        } else {
            this.setState({
                activeLink: null,
                activePosition: -100
            });
        }
    }

    private _isActiveLink(link: string) {
        return new RegExp(`^/?${this.state.activeLink}/?$`).test(link);
    }

    private _handleLinkClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>, config: SideMenuLinkConfiguration) {
        this._setActiveLink(config.link);
        this.services.sideMenu.getLinkAction(config)(e);
        this.services.analytics.sendAnalytics('Navigation', 'NavBar', config.text);
    }

    private _renderLink(config: SideMenuLinkConfiguration) {

        const isActive = this._isActiveLink(config.link);

        return (
            <StickyNavigationMenuItemBox key={config.text} ref={e => this._setActiveLinkRef(e, isActive)} onClick={e => this._handleLinkClick(e, config)} isActive={isActive} type={config.class}>
            <StickyNavigationMenuItemIconBox icon={config.class} isActive={isActive} />
            <Link>
            {this.services.translation.translate(config.text)}
            </Link>
            </StickyNavigationMenuItemBox>
        );
    }

    private _renderLinksList() {

        this.allMenuLinks = this.services.configuration.data.sideMenu.menu_links;

        const linksOrder = this.services.configuration.data.sideMenu.stickyMenuBlockOrder[0] || [];
        const linksConfig = this.services.configuration.pickSideMenuItems(linksOrder);

        if (this.validNavPaths.length === 0) {
            this.validNavPaths = linksOrder.filter(link => this.allMenuLinks[link]).map(link => this.allMenuLinks[link].link);
        }

        return linksConfig.filter(item => item.config.display).map(item => this._renderLink(item.config));
    }

    render() {
        return (
            <React.Fragment>
            <StickyNavigationMenuActiveIndicatorComponent position={this.state.activePosition} />
            <StickyNavigationMenuListBox ref={el => this._setStickyMenuContainerRef(el)}>
            {this._renderLinksList()}
            </StickyNavigationMenuListBox>
            </React.Fragment>
        );
    }
}
